/**
 * TablePagination – lightweight pagination bar for report tables with
 * optional auto-advance on scroll, page size selection, and direct page jump.
 *
 * Props:
 *   currentPage         - current 1-based page number
 *   totalRows           - total number of rows in the filtered dataset
 *   pageSize            - rows per page (default: 100)
 *   onPageChange        - callback(newPage)
 *   pageSizeOptions     - optional array of page sizes (e.g. [50, 100, 250, 500])
 *   onPageSizeChange    - optional callback(newSize)
 *   autoAdvance         - boolean indicating whether auto-advance on bottom scroll is active
 *   onToggleAutoAdvance - optional callback() to toggle auto-advance mode
 *   isFlipping          - optional boolean flag indicating page is actively auto-advancing
 */
import React, { useState, useEffect } from "react";

function TablePagination({
  currentPage = 1,
  totalRows = 0,
  pageSize = 100,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  autoAdvance = false,
  onToggleAutoAdvance,
  isFlipping = false,
}) {
  const safePageSize = Number(pageSize) > 0 ? Number(pageSize) : 100;
  const safeTotalRows = Number(totalRows) > 0 ? Number(totalRows) : 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalRows / safePageSize));

  const [jumpInput, setJumpInput] = useState(String(currentPage));

  useEffect(() => {
    setJumpInput(String(currentPage));
  }, [currentPage]);

  if (safeTotalRows === 0) return null;
  if (totalPages <= 1 && !pageSizeOptions && !onToggleAutoAdvance) return null;

  const from = Math.min((currentPage - 1) * safePageSize + 1, safeTotalRows);
  const to = Math.min(currentPage * safePageSize, safeTotalRows);

  // Generate a bounded window of page numbers (never allocates huge arrays)
  const getPageNumbers = () => {
    const delta = 1;
    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    const items = [1];
    if (left > 2) items.push("…");
    for (let p = left; p <= right; p++) {
      items.push(p);
    }
    if (right < totalPages - 1) items.push("…");
    if (totalPages > 1) items.push(totalPages);
    return items;
  };

  const pageItems = getPageNumbers();

  const handleJumpSubmit = (e) => {
    if (e) e.preventDefault();
    const val = parseInt(jumpInput, 10);
    if (!isNaN(val) && val >= 1 && val <= totalPages) {
      if (val !== currentPage) {
        onPageChange(val);
      }
    } else {
      setJumpInput(String(currentPage));
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between px-3 py-1.5 bg-white border-t border-slate-200 text-[11px] font-Inter font-semibold text-slate-600 shrink-0 gap-2 select-none">
      {/* Left section: Records count & Per-page selector & Auto-flip toggle */}
      <div className="flex flex-wrap items-center gap-2.5">
        <span>
          Showing <span className="text-[#1e73be] font-bold">{from}–{to}</span> of{" "}
          <span className="text-slate-800 font-bold">{safeTotalRows}</span> records
        </span>

        {pageSizeOptions && onPageSizeChange && (
          <div className="flex items-center gap-1 text-[10px] text-slate-500">
            <span>Per page:</span>
            <select
              value={safePageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700 text-[10px] font-semibold cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Auto-Advance on Scroll indicator / toggle */}
        {onToggleAutoAdvance && (
          <button
            type="button"
            onClick={onToggleAutoAdvance}
            className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold border transition-all cursor-pointer ${
              autoAdvance
                ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 shadow-2xs"
                : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
            }`}
            title="When active: scrolling to the bottom of the page automatically advances to the next page smoothly without lag"
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                autoAdvance ? "bg-emerald-500 animate-pulse" : "bg-slate-400"
              }`}
            />
            <span>Auto-Advance: {autoAdvance ? "ON" : "OFF"}</span>
          </button>
        )}

        {/* Transient Flipping Indicator */}
        {isFlipping && (
          <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 animate-pulse">
            Flipping to page {currentPage}...
          </span>
        )}
      </div>

      {/* Right section: Page buttons & Jump-to-page input */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={currentPage <= 1}
            className="px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-bold cursor-pointer transition-colors"
            title="First page"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-bold cursor-pointer transition-colors"
            title="Previous page"
          >
            ‹ Prev
          </button>

          {pageItems.map((item, idx) =>
            item === "…" ? (
              <span key={`ellipsis-${idx}`} className="px-1 text-slate-400 select-none">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onPageChange(item)}
                className={`px-2 py-0.5 rounded border text-[10px] font-bold cursor-pointer transition-colors ${
                  item === currentPage
                    ? "bg-[#1e73be] text-white border-[#1e73be]"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-bold cursor-pointer transition-colors"
            title="Next page"
          >
            Next ›
          </button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage >= totalPages}
            className="px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-bold cursor-pointer transition-colors"
            title="Last page"
          >
            »
          </button>

          {/* Direct Page Jump Input */}
          {totalPages > 3 && (
            <form onSubmit={handleJumpSubmit} className="flex items-center gap-1 ml-1 text-[10px] text-slate-500">
              <span className="text-slate-400">Page:</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={jumpInput}
                onChange={(e) => setJumpInput(e.target.value)}
                onBlur={handleJumpSubmit}
                className="w-10 px-1 py-0.5 text-center bg-white border border-slate-300 rounded text-[10px] font-bold text-slate-800 focus:outline-none focus:border-blue-500"
                title={`Enter page number (1 to ${totalPages})`}
              />
              <span className="text-slate-400">/{totalPages}</span>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default TablePagination;
